import { HttpErrorResponse } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';

import { environment } from '@env';
import { AnalyticsApiService } from './analytics-api.service';

export type DashboardAccess =
  /** Вход выключен (`npm start`): пользователя нет, ограничивать нечего. */
  | { readonly kind: 'open' }
  | { readonly kind: 'loading' }
  | { readonly kind: 'ready'; readonly keys: ReadonlySet<string> }
  /** Пустой скоуп — API отвечает 403, а не пустым списком. */
  | { readonly kind: 'forbidden' }
  | { readonly kind: 'error'; readonly message: string };

/**
 * Какие дашборды Vasta.Analytics.Api доступны пользователю — по `GET /dashboards`.
 * Ограничение одно для «Демо» и «Сервера»: доступ зависит от учётной записи, не от источника.
 */
@Injectable({ providedIn: 'root' })
export class DashboardAccessService {
  private readonly api = inject(AnalyticsApiService);

  readonly state = signal<DashboardAccess>(
    environment.authEnabled ? { kind: 'loading' } : { kind: 'open' },
  );

  private pending: Promise<DashboardAccess> | null = null;

  ensure(): Promise<DashboardAccess> {
    const state = this.state();

    if (state.kind !== 'loading') {
      return Promise.resolve(state);
    }

    this.pending ??= firstValueFrom(this.api.dashboards()).then(
      (dashboards): DashboardAccess => ({
        kind: 'ready',
        keys: new Set(dashboards.map((dashboard) => dashboard.key)),
      }),
      (error: unknown): DashboardAccess =>
        error instanceof HttpErrorResponse && error.status === 403
          ? { kind: 'forbidden' }
          : { kind: 'error', message: accessError(error) },
    );

    return this.pending.then((access) => {
      this.state.set(access);
      this.pending = null;

      return access;
    });
  }

  retry(): Promise<DashboardAccess> {
    this.state.set({ kind: 'loading' });

    return this.ensure();
  }

  /**
   * Виден только дашборд из ответа, включая hotels и restaurants.
   * Отсутствие ключа не даёт доступа через старый Flash API.
   */
  allows(apiKey: string | undefined, access: DashboardAccess = this.state()): boolean {
    if (access.kind === 'open') {
      return true;
    }

    return access.kind === 'ready' && apiKey !== undefined && access.keys.has(apiKey);
  }
}

function accessError(error: unknown): string {
  if (error instanceof HttpErrorResponse) {
    switch (error.status) {
      case 401:
        return 'Сессия истекла: войдите заново';
      case 503:
        return 'Сервис аналитики недоступен';
    }
  }

  return 'Не удалось получить список дашбордов';
}
